import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Circular percentage ring. Without `label` it is the analysis progress bar; with `label`
 * it shows a finished score and is announced as "<label>: <value>%".
 * `isStatic` (PDF export) and the reduced-motion setting skip the fill animation.
 */
// The repo declares no propTypes (see the eslint baseline); keep this component from adding new findings
// eslint-disable-next-line react/prop-types
export function AIProgressRing({ progress = 0, size = 120, strokeWidth = 8, className = "", label, isStatic = false }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const reduceMotion = useReducedMotion();
  const animateFill = !isStatic && !reduceMotion;
  const value = Math.round(progress);
  const a11yProps = label
    ? { role: "img", "aria-label": `${label}: ${value}%` }
    : { role: "progressbar", "aria-valuenow": value, "aria-valuemin": "0", "aria-valuemax": "100", "aria-label": "Analysis progress" };

  return (
    <div
      className={cn("relative flex items-center justify-center", className)}
      style={{ width: size, height: size }}
      {...a11yProps}
    >
      <svg width={size} height={size} aria-hidden="true">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="rgba(31,76,84,0.12)"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="var(--accent)"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            initial={animateFill ? { strokeDashoffset: circumference } : false}
            animate={{ strokeDashoffset }}
            transition={animateFill ? { duration: 0.8, ease: "easeOut" } : { duration: 0 }}
          />
        </g>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.span
          className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)]"
          initial={animateFill ? { opacity: 0, scale: 0.6 } : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: animateFill ? 0.4 : 0 }}
        >
          {value}%
        </motion.span>
      </div>
    </div>
  );
}

export function AnalysisProgress({ steps, currentStep = 0, className = "" }) {
  return (
    <div className={cn("space-y-4 text-left", className)}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;

        return (
          <motion.div
            key={step.title}
            className="flex items-start gap-4"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
          >
            <span
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-2xl border text-sm font-semibold",
                isCompleted && "border-[rgba(31,76,84,0.18)] bg-[rgba(31,76,84,0.08)] text-[var(--accent)]",
                isActive && "border-[rgba(31,76,84,0.3)] bg-white text-[var(--accent)] shadow-[0_12px_24px_rgba(15,23,42,0.12)]",
                !isCompleted && !isActive && "border-[rgba(31,76,84,0.12)] text-[var(--text-soft)]"
              )}
            >
              {isCompleted ? (
                <Check className="h-4 w-4" />
              ) : isActive ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                index + 1
              )}
            </span>
            <div className="space-y-1">
              <p
                className={cn(
                  "text-sm font-medium",
                  isCompleted || isActive
                    ? "text-[var(--text-primary)]"
                    : "text-[var(--text-soft)]"
                )}
              >
                {step.title}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">{step.description}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
